import { Eye, EyeOff, LoaderCircle, LockKeyhole } from 'lucide-react';
import {
  useId,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../lib/utils';
import { hexToHsl } from '../utils/hex-to-hsl';
import { PortalBrand } from './portal-brand';
import type { ResolvedBranding } from './types';

export type AuthFormResult = { ok: true } | { ok: false; error: string };

interface AuthScreenProps {
  branding: ResolvedBranding;
  description: string;
  children: ReactNode;
  eyebrow?: string;
  footer?: string;
  className?: string;
}

/**
 * Shared, presentation-only auth shell. Authentication and authorization stay
 * inside each app; this component only renders its configured branding and
 * form content.
 */
export function AuthScreen({
  branding,
  description,
  children,
  eyebrow = 'Secure workspace',
  footer = 'Command Center · Secure sign-in',
  className,
}: AuthScreenProps) {
  const style = authBrandStyle(branding);
  return (
    <main
      style={style}
      className={cn(
        'relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-8 sm:px-6',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,hsl(var(--primary)/0.12),transparent_34%),radial-gradient(circle_at_bottom_right,hsl(var(--accent)/0.28),transparent_38%)]" />
      <div className="relative grid w-full max-w-4xl overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_24px_80px_-38px_hsl(var(--foreground)/0.35)] md:grid-cols-[0.9fr_1.1fr]">
        <section className="relative flex min-h-52 flex-col justify-between overflow-hidden bg-primary p-7 text-primary-foreground sm:p-9 md:min-h-[560px]">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full border border-primary-foreground/10" />
          <div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-primary-foreground/[0.06]" />
          <div className="relative">
            <div className="inline-flex max-w-full rounded-xl bg-card px-3 py-2 text-card-foreground shadow-sm">
              <PortalBrand branding={branding} />
            </div>
            <p className="mt-10 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/70">
              {eyebrow}
            </p>
            <h1 className="mt-3 max-w-sm text-2xl font-semibold tracking-tight sm:text-3xl">
              {branding.name}
            </h1>
            <p className="mt-4 max-w-sm text-sm leading-6 text-primary-foreground/78">
              {description}
            </p>
          </div>
          <p className="relative mt-10 text-xs text-primary-foreground/60">{footer}</p>
        </section>

        <section className="flex items-center p-6 sm:p-10 md:p-12">
          <div className="mx-auto w-full max-w-sm">{children}</div>
        </section>
      </div>
    </main>
  );
}

function authBrandStyle(branding: ResolvedBranding): CSSProperties {
  const values: Record<string, string> = {};
  const set = (token: string, brandToken: string, value?: string) => {
    const hsl = value ? hexToHsl(value) : null;
    if (!hsl) return;
    values[token] = hsl;
    values[brandToken] = hsl;
  };
  set('--primary', '--brand-primary', branding.colors?.primary);
  set('--ring', '--brand-ring', branding.colors?.primary);
  set('--background', '--brand-background', branding.colors?.background);
  set('--accent', '--brand-accent', branding.colors?.accent);
  return values as CSSProperties;
}

interface AuthSignInFormProps {
  description: string;
  configured: boolean;
  onSignIn(email: string, password: string): Promise<AuthFormResult>;
  onForgotPassword?: (email: string) => Promise<AuthFormResult>;
  configurationMessage?: string;
}

export function AuthSignInForm({
  description,
  configured,
  onSignIn,
  onForgotPassword,
  configurationMessage = 'Sign-in is temporarily unavailable. Please contact your portal administrator.',
}: AuthSignInFormProps) {
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState<'sign-in' | 'reset'>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(undefined);
    setNotice(undefined);
    setBusy('sign-in');
    try {
      const result = await onSignIn(email.trim(), password);
      if (!result.ok) setError(result.error);
    } finally {
      setBusy(undefined);
    }
  }

  async function forgotPassword() {
    if (!onForgotPassword || busy) return;
    setError(undefined);
    setNotice(undefined);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Enter your email address above, then choose “Forgot password?”.');
      return;
    }
    setBusy('reset');
    try {
      const result = await onForgotPassword(trimmedEmail);
      if (result.ok) setNotice('Check your email for a password reset link.');
      else setError(result.error);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <form className="grid gap-5" onSubmit={submit} aria-label="Sign in">
      <AuthFormHeading title="Welcome back" description={description} />

      {!configured && <AuthNotice tone="warning">{configurationMessage}</AuthNotice>}

      <div className="grid gap-4">
        <label className="grid gap-1.5 text-sm font-medium text-foreground" htmlFor={emailId}>
          Email
        </label>
        <Input
          id={emailId}
          className="h-11"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={Boolean(busy) || !configured}
        />

        <div className="grid gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-foreground" htmlFor={passwordId}>
              Password
            </label>
            {onForgotPassword && (
              <button
                type="button"
                className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={Boolean(busy) || !configured}
                onClick={() => void forgotPassword()}
              >
                {busy === 'reset' ? 'Sending…' : 'Forgot password?'}
              </button>
            )}
          </div>
          <PasswordInput
            id={passwordId}
            name="password"
            autoComplete="current-password"
            minLength={6}
            value={password}
            onChange={setPassword}
            disabled={Boolean(busy) || !configured}
            visible={showPassword}
            onVisibleChange={setShowPassword}
          />
        </div>
      </div>

      {error && <AuthNotice tone="error">{error}</AuthNotice>}
      {notice && <AuthNotice>{notice}</AuthNotice>}

      <Button className="h-11 w-full" type="submit" disabled={Boolean(busy) || !configured}>
        {busy === 'sign-in' && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />}
        {busy === 'sign-in' ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}

interface AuthPasswordFormProps {
  title: string;
  description?: string;
  submitLabel?: string;
  busyLabel?: string;
  onSubmit(password: string): Promise<AuthFormResult>;
}

export function AuthPasswordForm({
  title,
  description = 'Use at least eight characters.',
  submitLabel = 'Update password',
  busyLabel = 'Updating…',
  onSubmit,
}: AuthPasswordFormProps) {
  const passwordId = useId();
  const confirmationId = useId();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(undefined);
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const result = await onSubmit(password);
      if (!result.ok) setError(result.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-5" onSubmit={submit} aria-label="Reset password">
      <AuthFormHeading title={title} description={description} />
      <div className="grid gap-4">
        <label className="grid gap-1.5 text-sm font-medium text-foreground" htmlFor={passwordId}>
          New password
          <PasswordInput
            id={passwordId}
            name="new-password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={setPassword}
            disabled={busy}
            visible={showPassword}
            onVisibleChange={setShowPassword}
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-foreground" htmlFor={confirmationId}>
          Confirm new password
          <PasswordInput
            id={confirmationId}
            name="confirm-password"
            autoComplete="new-password"
            minLength={8}
            value={confirmation}
            onChange={setConfirmation}
            disabled={busy}
            visible={showConfirmation}
            onVisibleChange={setShowConfirmation}
          />
        </label>
      </div>
      {error && <AuthNotice tone="error">{error}</AuthNotice>}
      <Button className="h-11 w-full" type="submit" disabled={busy}>
        {busy && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />}
        {busy ? busyLabel : submitLabel}
      </Button>
    </form>
  );
}

export function AuthLinkError({
  message,
  onReturn,
}: {
  message: string;
  onReturn: () => void;
}) {
  return (
    <div className="grid gap-5 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <LockKeyhole className="h-5 w-5" aria-hidden />
      </div>
      <AuthFormHeading
        title="We couldn’t open that link"
        description="The link may be expired, already used, or incomplete."
      />
      <AuthNotice tone="error">{message}</AuthNotice>
      <Button type="button" className="h-11 w-full" onClick={onReturn}>
        Return to sign in
      </Button>
    </div>
  );
}

function AuthFormHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

function AuthNotice({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'warning' | 'error';
}) {
  return (
    <p
      className={cn(
        'rounded-lg border p-3 text-sm leading-5',
        tone === 'neutral' && 'border-border bg-muted/45 text-foreground',
        tone === 'warning' && 'border-warning/40 bg-warning/10 text-foreground',
        tone === 'error' && 'border-destructive/30 bg-destructive/5 text-destructive',
      )}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      {children}
    </p>
  );
}

interface PasswordInputProps {
  id: string;
  name: string;
  autoComplete: string;
  minLength: number;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
}

function PasswordInput({
  id,
  name,
  autoComplete,
  minLength,
  value,
  onChange,
  disabled,
  visible,
  onVisibleChange,
}: PasswordInputProps) {
  return (
    <div className="relative">
      <Input
        id={id}
        className="h-11 pr-11 font-normal"
        type={visible ? 'text' : 'password'}
        name={name}
        autoComplete={autoComplete}
        minLength={minLength}
        required
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        disabled={disabled}
        onClick={() => onVisibleChange(!visible)}
      >
        {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
      </button>
    </div>
  );
}
